import { Component, DestroyRef, ElementRef, inject, Input, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Subject, combineLatest, of, takeUntil } from 'rxjs';
import { IProduct } from 'src/app/models/products/product';
import { IAsset } from 'src/app/models/products/asset';
import { ITouch } from 'src/app/models/products/touch';
import { AuthService } from 'src/app/services/auth.service';
import { Permission } from 'src/app/constants/permission.constant';
import { ProductsService } from 'src/app/services/products.service';
import { MasterSuitesService } from 'src/app/services/mastersuites.service';
import { SignalrService } from 'src/app/services/signalr.service';
import { SignalrGroup } from 'src/app/services/signalr-group';
import { SignalRIdGroups } from 'src/app/constants/signalr.constant';
import { EnhancementVariantDetail } from 'src/app/models/mastersuites/mastersuite';
import {
  parseAsset,
  parseStepContentData,
  setAllSelected,
  selectAllInTouch,
  onCardSelected,
  checkAllSelected,
  countSelectedAssets,
  isDownloadDisabled,
  hasSelectableAssets,
  ParseAssetContext,
} from '../product-asset.utils';

@Component({
    selector: 'app-step-post-production',
    templateUrl: './step-post-production.component.html',
    styleUrls: ['./step-post-production.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
    standalone: false
})
export class StepPostProductionComponent implements OnInit, OnDestroy {
  @Input() product!: IProduct;
  @Input() colPerScreen = 3;
  @Input() editCallback!: (data: any) => void;
  @ViewChild('cardItem') cardItemEl!: ElementRef;

  Permission = Permission;
  content: any[] = [];
  listPostProduction: any[] = [];
  isLoading = false;
  allSelected = false;
  selectedCount = 0;
  displaySelectTouch: { [key: string]: boolean } = {};
  enhancementVariantIdSelected: string | null = '';
  enhancementVariants!: EnhancementVariantDetail[];
  postView = 'selects';
  rowHeight = 'fit';

  private readonly destroyRef = inject(DestroyRef);
  private destroy$ = new Subject();

  constructor(
    public authService: AuthService,
    private productsService: ProductsService,
    private masterSuiteService: MasterSuitesService,
    private signalrService: SignalrService,
  ) {}

  ngOnInit(): void {
    this.isLoading = true;
    this.getPostProductionData();
    this.getEnhancementVariant();
    this.joinAndSubscribeToSignalR();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
    this.signalrService.clearPostAssets();
  }

  onViewChange(): void {
    if (this.postView === 'selects') {
      this.getPostProductionData();
    } else {
      this.getAllPostProductionData();
    }
  }

  getPostProductionData(): void {
    if (!this.product.id) return;

    this.listPostProduction = [];
    const autoPostAssets$ = this.authService.hasPermission(Permission.AutoPostAssetsRead)
      ? this.productsService.getListAutoPostProduction(this.product.id)
      : of([] as IAsset[]);
    combineLatest([
      this.productsService.getListPostProduction(this.product.id),
      autoPostAssets$,
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([value, autoValue]: [IAsset[], IAsset[]]) => {
        const selectsGroup = this.buildProductionGroup('Selects', this.flattenAssets(value));
        const groups = this.hasAutoPostData(autoValue)
          ? [selectsGroup, this.buildProductionGroup('Automated Selects', this.flattenAssets(autoValue))]
          : [selectsGroup];

        this.listPostProduction = groups;
        this.updateContentAndSelection(this.listPostProduction);
      });
  }

  getAllPostProductionData(): void {
    if (!this.product.id) return;

    this.productsService
      .getListAllPostProduction(this.product.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (value: ITouch[]) => {
          const listData = value.map((touch) =>
            ({ ...touch, ...this.buildProductionGroup(touch.name, this.flattenAssets(touch.assets)) }),
          );
          this.updateContentAndSelection(listData);
        },
      });
  }

  private get parseContext(): ParseAssetContext {
    return {
      step: 'postProduction',
      listProduction: [],
      hasPreReleasePermission: this.authService.hasPermission(Permission.PostAssetsPreRelease),
    };
  }

  private resetSelection(): void {
    this.allSelected = false;
    this.selectedCount = 0;
    this.isLoading = false;
  }

  getEnhancementVariant(): void {
    if (!this.product.masterSuiteId) return;
    this.masterSuiteService
      .getEnhancementVariantOfMasterSuite({ MasterSuiteId: this.product.masterSuiteId })
      .subscribe({
        next: (res) => {
          this.enhancementVariants = res.results;
          this.enhancementVariantIdSelected = this.product.masterSuiteEnhancementVariantId;
        },
      });
  }

  private joinAndSubscribeToSignalR(): void {
    if (!this.authService.hasPermission(Permission.PostAssetsPreRelease)) return;
    SignalrGroup.joinWithId({
      signalrService: this.signalrService,
      destroyRef: this.destroyRef,
      group: SignalRIdGroups.ProductPostAssets,
      id: this.product.id!,
      data$: this.signalrService.postAssetsData,
      onData: (data: any) => {
        if (!data.id) return;
        if (!this.content[0]) return;

        const index = this.content[0].assets.findIndex(
          (a: IAsset) => a.masterSuiteTouchAssetId === data.masterSuiteTouchAssetId,
        );
        if (index < 0) return;

        const assetData: any[] = [{ ...data }];
        const asset = parseAsset(assetData, this.parseContext)[0];
        if (this.content[0].assets[index].id === asset.id) {
          this.content[0].assets[index].clientReviewState = asset.clientReviewState;
          this.content[0].assets[index].internalReviewState = asset.internalReviewState;
        } else {
          this.content[0].assets[index] = asset;
        }
      },
    });
  }

  setAll(completed: boolean): void {
    const result = setAllSelected(this.content, completed);
    this.allSelected = completed;
    this.selectedCount = result.count;
  }

  selectedAllTouch(completed: boolean, touch: any): void {
    selectAllInTouch(this.content, touch.id, completed);
    this.allSelected = checkAllSelected(this.content);
    this.selectedCount = countSelectedAssets(this.content);
  }

  selectedCard(card: { touchId: string; data: any }): void {
    onCardSelected(this.content, card.touchId);
    this.allSelected = checkAllSelected(this.content);
    this.selectedCount = countSelectedAssets(this.content);
  }

  getSelectedPostAssetIds(): string[] {
    return this.content.flatMap((touch: any) =>
      touch.assets
        .filter((asset: any) => asset.selected && !asset.childIndex && asset.id)
        .map((asset: any) => asset.id),
    );
  }

  disableButtonDownload(): boolean {
    return isDownloadDisabled(this.content);
  }

  downloadMultiple(): void {
    const selectedAssets = this.content.flatMap((touch: any) =>
      touch.assets.filter((asset: any) => asset.selected),
    );
    selectedAssets.reduce((delay: number, asset: any) => {
      const fileName = this.buildDownloadFileName(asset);
      setTimeout(() => this.downloadAsset(asset, fileName), delay);
      return delay + 3000;
    }, 3000);
  }

  private buildDownloadFileName(asset: any): string {
    const pathSegments = asset.rawUri.split('?')[0].split('/');
    const fileParts = pathSegments[pathSegments.length - 1].split('.');
    return `${fileParts[0]}_${asset.index / 1000 + 1}.${fileParts[1]}`;
  }

  private downloadAsset(asset: any, fileName: string): void {
    this.productsService
      .downloadSingleImage(asset.rawUri.split('//')[1])
      .subscribe((res) => {
        const blob: Blob = res.body as Blob;
        const anchor = document.createElement('a');
        const fileData = window.URL.createObjectURL(blob);
        anchor.href = fileData;
        anchor.download = fileName;
        anchor.click();
        window.URL.revokeObjectURL(fileData);
      });
  }

  private flattenAssets(assets: IAsset[]): any[] {
    return assets.flatMap((asset) => [
      { ...asset, selected: false },
      ...(asset.copyAssets?.length
        ? asset.copyAssets.map((copyAsset, index) => ({ ...asset, ...copyAsset, childIndex: index + 1, selected: false }))
        : []),
    ]);
  }

  private buildProductionGroup(name: string, assets: any[]): any {
    return {
      name,
      assets,
      selectedTouch: false,
      quantitySelected: 0,
    };
  }

  private hasAutoPostData(autoValue: IAsset[]): boolean {
    return !autoValue.every((asset: any) => asset.id == null);
  }

  private updateContentAndSelection(listData: any[]): void {
    this.content = parseStepContentData(listData, this.parseContext);
    this.displaySelectTouch = listData.reduce((acc: { [key: string]: boolean }, group: any) => ({
      ...acc,
      [group.id]: hasSelectableAssets(this.content, group.id),
    }), {});
    this.resetSelection();
  }

  onEnhancementVariantChanged(enhancementVariantId: string | null): void {
    this.enhancementVariantIdSelected = enhancementVariantId;
    this.product.masterSuiteEnhancementVariantId = this.enhancementVariantIdSelected;
    this.isLoading = true;
    this.getPostProductionData();
  }

  updateRowHeight(): void {
    setTimeout(() => {
      if (!this.cardItemEl) return;
      this.rowHeight = `${this.cardItemEl.nativeElement.firstChild.offsetWidth}:${this.cardItemEl.nativeElement.firstChild.offsetHeight}`;
      this.isLoading = false;
    });
  }
}
